/**
 * LIVING CHAT — the v2 cast on the real L9 backend (ADR-0021, ADR-0026, V2-F9).
 *
 * The view owns no data and invents nothing. It reads the server's scheduled
 * presence (``GET /core/chat/presence``), the player's real threads
 * (``GET /core/chat/threads``) and one thread's messages in server order
 * (``GET /core/chat/threads/:characterId/messages``), then renders exactly
 * those rows. Character names and the intermittent/codec flags come from the
 * published content pack; a missing name falls back to the server id.
 *
 * A thread may carry character-to-character lines (ADR-0021 §5) and Andrea's
 * codec intrusions (ADR-0021 §4). The view marks them from the stored sender,
 * never from a client guess. Offline, v2-off, no presence, no thread and no
 * messages each render an honest empty state.
 */
import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import { isMobileDevice } from '../device';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const CELL_W = 8;
const LINE_H = 16;
const MSG_GAP = 6;
const MAX_COMPOSE = 500;
const HOLDING_WINDOW_MS = 8000;
const COMPOSE_INPUT_ID = 'dcw-living-chat-input';

const state = {
  pack: null,
  presence: null,
  cast: [],
  threads: [],
  activeId: null,
  thread: null,
  messages: [],
  loaded: false,
  loading: false,
  threadLoading: false,
  threadLoadedFor: null,
  error: null,
  threadError: null,
  v2Off: false,
  scrollOffset: 0,
  maxScroll: 0,
  composeText: '',
  composeActive: false,
  sending: false,
  sendStartedAt: 0,
  sendError: null,
  noReply: false,
  lastReply: null,
};

let loadPromise = null;

function removeComposeInput() {
  if (typeof document === 'undefined') return;
  const element = document.getElementById(COMPOSE_INPUT_ID);
  if (element?.parentNode) element.parentNode.removeChild(element);
}

export function _resetLivingChatState(overrides = {}) {
  Object.assign(
    state,
    {
      pack: null,
      presence: null,
      cast: [],
      threads: [],
      activeId: null,
      thread: null,
      messages: [],
      loaded: false,
      loading: false,
      threadLoading: false,
      threadLoadedFor: null,
      error: null,
      threadError: null,
      v2Off: false,
      scrollOffset: 0,
      maxScroll: 0,
      composeText: '',
      composeActive: false,
      sending: false,
      sendStartedAt: 0,
      sendError: null,
      noReply: false,
      lastReply: null,
    },
    overrides,
  );
  removeComposeInput();
  loadPromise = null;
}

export function livingChatErrorMessage(error) {
  if (!error) return 'COMMS LINE DOWN';
  if (error.code === 'CORE-404-012') return 'COMMS CLOSED // V2 OFFLINE';
  return coreErrorMessage(error);
}

export function replyErrorMessage(error) {
  if (!error) return 'LINE DOWN';
  if (error.code === 'CORE-404-012') return 'COMMS CLOSED // V2 OFFLINE';
  if (error.status === 429) return 'HAL AT CAP // TRY AGAIN LATER';
  if (error.status === 408 || error.status === 504 || error.name === 'AbortError') {
    return 'LINE TIMED OUT // NO LINE INVENTED';
  }
  return coreErrorMessage(error);
}

export function replyOutcomeLabel(outcome) {
  if (outcome === 'answered') return 'ANSWERED';
  if (outcome === 'holding') return 'HOLDING';
  if (outcome === 'follow_up') return 'FOLLOW-UP';
  if (outcome === 'fallback') return 'FALLBACK';
  return null;
}

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function fit(value, maxChars) {
  const string = String(value || '');
  if (maxChars <= 0) return '';
  return string.length <= maxChars ? string : string.slice(0, maxChars);
}

export function wrapText(value, maxChars = 40) {
  const words = String(value || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    if (!line) {
      line = word;
    } else if (`${line} ${word}`.length <= maxChars) {
      line = `${line} ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
}

/** Character metadata from the published pack; the id is the only fallback. */
export function characterProfile(pack, characterId) {
  const id = String(characterId || '');
  const row = (Array.isArray(pack?.characters) ? pack.characters : []).find(
    (character) => character?.characterId === id,
  );
  return {
    characterId: id,
    name: row?.name ? String(row.name) : id.toUpperCase(),
    role: row?.role ? String(row.role) : null,
    intermittent: Boolean(row?.intermittent || row?.codec_style),
    codec: Boolean(row?.codec_style),
  };
}

export function normalizeStatus(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'online' || value === 'away' || value === 'offline') return value;
  return 'offline';
}

export function statusLabel(status) {
  if (status === 'online') return 'ONLINE';
  if (status === 'away') return 'AWAY';
  return 'OFFLINE';
}

export function formatLastSeen(value) {
  const raw = String(value || '');
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const iso = date.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}Z`;
}

/** True when the thread stored a character other than its owner speaking. */
export function threadHasExchange(messages, ownerId) {
  const speakers = new Set(
    (Array.isArray(messages) ? messages : [])
      .filter((message) => message?.sender === 'character' && message?.senderId)
      .map((message) => message.senderId),
  );
  if (speakers.size > 1) return true;
  return Boolean(ownerId && speakers.size === 1 && !speakers.has(ownerId));
}

export function hasCodecIntrusion(messages, pack) {
  return (Array.isArray(messages) ? messages : []).some(
    (message) =>
      message?.sender === 'character' &&
      characterProfile(pack, message?.senderId).codec,
  );
}

/** Merge real presence rows with the published character names. */
export function mapPresence(payload, pack = null) {
  const rows = Array.isArray(payload?.characters) ? payload.characters : [];
  return rows
    .filter((row) => row?.characterId)
    .map((row) => {
      const profile = characterProfile(pack, row.characterId);
      return {
        ...profile,
        status: normalizeStatus(row.status),
        lastSeenAt: row.lastSeenAt || null,
      };
    })
    .sort((left, right) => {
      const order = { online: 0, away: 1, offline: 2 };
      const delta = order[left.status] - order[right.status];
      if (delta !== 0) return delta;
      return left.name.localeCompare(right.name);
    });
}

/** Threads as the server returned them; no row is created for a cast member. */
export function mapThreads(payload, pack = null) {
  const rows = Array.isArray(payload?.threads) ? payload.threads : [];
  return rows
    .filter((row) => row?.characterId)
    .map((row) => {
      const profile = characterProfile(pack, row.characterId);
      return {
        ...row,
        ...profile,
        messageCount: Number(row.messageCount || 0),
      };
    })
    .sort((left, right) => {
      const leftAt = String(left.lastMessageAt || '');
      const rightAt = String(right.lastMessageAt || '');
      if (leftAt !== rightAt) return leftAt < rightAt ? 1 : -1;
      return String(left.threadId || '').localeCompare(String(right.threadId || ''));
    });
}

export function mapMessages(payload, pack, ownerId) {
  const rows = Array.isArray(payload?.messages) ? payload.messages : [];
  return rows.map((message) => {
    const profile =
      message?.sender === 'character'
        ? characterProfile(pack, message.senderId)
        : { characterId: null, name: 'YOU', codec: false };
    return {
      ...message,
      senderName: profile.name,
      codec: Boolean(profile.codec),
      exchange: Boolean(
        ownerId && message?.sender === 'character' && message.senderId !== ownerId,
      ),
    };
  });
}

function presenceFor(characterId) {
  return state.cast.find((row) => row.characterId === characterId) || null;
}

function threadFor(characterId) {
  return state.threads.find((row) => row.characterId === characterId) || null;
}

/**
 * Why the compose line is locked, or null when the player may type.
 *
 * The server only answers from a scheduled, online character (V2-L9b), so the
 * client follows the same rule instead of offering a line that cannot reply.
 */
export function composeLockReason(
  session = useCoreSession.getState(),
  characterId = state.activeId,
) {
  if (state.sending) return 'HAL IS READING...';
  if (!session || session.status !== 'online' || !session.token) return 'NO LINE // OFFLINE';
  if (state.v2Off) return 'V2 OFFLINE // COMPOSE LOCKED';
  if (state.loading || !state.loaded) return 'READING THE CAST...';
  if (state.error) return 'LINE DOWN // COMPOSE LOCKED';
  if (!characterId) return 'NO CHANNEL // COMPOSE LOCKED';
  const presence = presenceFor(characterId);
  if (!presence) return 'ABSENT // NO REPLY';
  if (presence.status === 'away') return 'AWAY // NO REPLY';
  if (presence.status !== 'online') return 'OFFLINE // NO REPLY';
  return null;
}

export function canComposeLivingChat(session, characterId) {
  return composeLockReason(session, characterId) === null;
}

/** Keep server rows in order and never show the same message id twice. */
export function mergeMessages(existing, incoming) {
  const seen = new Set();
  const merged = [];
  [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(incoming) ? incoming : [])]
    .forEach((message) => {
      const id = message?.messageId;
      if (!id || seen.has(id)) return;
      seen.add(id);
      merged.push(message);
    });
  return merged;
}

/** Cast rows for the rail: real presence plus real threads with no shift. */
export function buildRail(cast = state.cast, threads = state.threads) {
  const rows = cast.map((row) => ({
    ...row,
    messageCount: threadFor(row.characterId)?.messageCount || 0,
  }));
  threads.forEach((thread) => {
    if (rows.some((row) => row.characterId === thread.characterId)) return;
    rows.push({
      ...thread,
      status: 'offline',
      lastSeenAt: null,
      offSchedule: true,
    });
  });
  return rows;
}

export function splitRail(rows = buildRail()) {
  return {
    cast: rows.filter((row) => !row.intermittent),
    signals: rows.filter((row) => row.intermittent),
  };
}

function pickActiveId(cast, threads) {
  const fromThread = threads[0]?.characterId;
  if (fromThread) return fromThread;
  const online = cast.find((row) => row.status === 'online');
  if (online) return online.characterId;
  return cast[0]?.characterId || null;
}

export async function loadLivingChatThread(characterId) {
  if (!characterId) return null;
  const session = useCoreSession.getState();
  if (!session.token) return null;
  state.threadLoading = true;
  state.threadError = null;
  try {
    const payload = await session.loadChatThread(characterId);
    if (state.activeId !== characterId) return payload;
    state.thread = payload?.thread || null;
    state.messages = mapMessages(payload, state.pack, characterId);
    state.threadLoadedFor = characterId;
    state.scrollOffset = 0;
    return payload;
  } catch (error) {
    if (error?.code === 'CORE-404-012') state.v2Off = true;
    state.threadError = livingChatErrorMessage(error);
    state.thread = null;
    state.messages = [];
    return null;
  } finally {
    state.threadLoading = false;
  }
}

export async function selectLivingChatCharacter(characterId) {
  if (!characterId) return;
  state.activeId = characterId;
  state.thread = null;
  state.messages = [];
  state.threadLoadedFor = null;
  state.threadError = null;
  state.scrollOffset = 0;
  state.composeText = '';
  state.composeActive = false;
  state.sendError = null;
  state.noReply = false;
  state.lastReply = null;
  await loadLivingChatThread(characterId);
}

/** Merge the POST response without inventing a timestamp or a line. */
export function applyReplyPayload(characterId, payload) {
  if (!payload || state.activeId !== characterId) return;
  if (payload.thread) state.thread = payload.thread;
  const incoming = [];
  if (payload.message?.messageId) incoming.push(payload.message);
  const reply = payload.reply;
  if (reply?.messageId) {
    incoming.push({
      messageId: reply.messageId,
      threadId: reply.threadId,
      sender: 'character',
      senderId: reply.characterId || characterId,
      body: reply.body,
    });
  }
  if (!incoming.length) return;
  state.messages = mapMessages(
    { messages: mergeMessages(state.messages, incoming) },
    state.pack,
    characterId,
  );
  state.threadLoadedFor = characterId;
}

async function reloadAfterReply(characterId) {
  const session = useCoreSession.getState();
  if (!session.token) return false;
  state.threadLoading = true;
  try {
    const payload = await session.loadChatThread(characterId);
    if (state.activeId !== characterId) return true;
    state.thread = payload?.thread || null;
    state.messages = mapMessages(payload, state.pack, characterId);
    state.threadLoadedFor = characterId;
    state.threadError = null;
    state.scrollOffset = 0;
    return true;
  } catch (error) {
    if (error?.code === 'CORE-404-012') state.v2Off = true;
    return false;
  } finally {
    state.threadLoading = false;
  }
}

/**
 * Send one player message through POST /chat/threads/:id/reply.
 *
 * The body is the only client input. The returned outcome is rendered as the
 * server sent it (answered, holding, follow_up or fallback); the thread is
 * reloaded afterwards so order and dedup stay server-authoritative. Nothing is
 * invented when the character is absent or the reply is null.
 */
export async function sendLivingChatMessage(raw) {
  const body = String(raw ?? '').trim();
  if (!body || body.length > MAX_COMPOSE) return null;
  const characterId = state.activeId;
  const session = useCoreSession.getState();
  if (!characterId || composeLockReason(session, characterId)) return null;

  state.sending = true;
  state.sendStartedAt = Date.now();
  state.sendError = null;
  state.noReply = false;
  state.lastReply = null;
  state.composeText = '';
  state.composeActive = false;
  releaseComposeInput();

  try {
    const payload = await session.replyChatThread(characterId, body);
    if (state.activeId !== characterId) return payload;
    state.lastReply = payload?.reply || null;
    state.noReply = !payload?.reply;
    applyReplyPayload(characterId, payload);
    const reloaded = await reloadAfterReply(characterId);
    if (!reloaded) applyReplyPayload(characterId, payload);
    return payload;
  } catch (error) {
    if (error?.code === 'CORE-404-012') state.v2Off = true;
    state.sendError = replyErrorMessage(error);
    state.composeText = body;
    return null;
  } finally {
    state.sending = false;
  }
}

export async function loadLivingChat({ force = false } = {}) {
  if (loadPromise) return loadPromise;
  if (state.loaded && !force) return null;
  if (force) {
    state.lastReply = null;
    state.noReply = false;
    state.sendError = null;
  }
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    state.threadError = null;
    state.v2Off = false;
    try {
      const session = useCoreSession.getState();
      if (session.status !== 'online' || !session.token) {
        state.loaded = true;
        return null;
      }

      let pack = session.content || null;
      if (!pack) {
        pack = await session.loadContent().catch(() => null);
      }
      state.pack = pack;

      let presence;
      try {
        presence = await session.loadChatPresence();
      } catch (error) {
        if (error?.code === 'CORE-404-012') {
          state.v2Off = true;
          state.loaded = true;
          return null;
        }
        throw error;
      }
      state.presence = presence;
      state.cast = mapPresence(presence, pack);

      const threadPayload = await session.loadChatThreads();
      state.threads = mapThreads(threadPayload, pack);

      const known = new Set([
        ...state.cast.map((row) => row.characterId),
        ...state.threads.map((row) => row.characterId),
      ]);
      if (!state.activeId || !known.has(state.activeId)) {
        state.activeId = pickActiveId(state.cast, state.threads);
      }
      state.loaded = true;

      if (state.activeId) {
        await loadLivingChatThread(state.activeId);
      }
      return null;
    } catch (error) {
      state.error = livingChatErrorMessage(error);
      state.loaded = true;
      return null;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

export function layoutLivingChat(width, height) {
  const mobile = width < 500;
  if (mobile) {
    const composeY = 474;
    return {
      mobile,
      top: { x: 8, y: 4, w: width - 16, h: 20 },
      rail: { x: 8, y: 30, w: width - 16, h: 140, rowH: 44, gap: 4, cols: 3 },
      pane: { x: 8, y: 176, w: width - 16, h: 290 },
      compose: {
        x: 8,
        y: composeY,
        w: width - 16,
        h: 44,
        input: { x: 8, y: composeY, w: width - 16 - 64, h: 44 },
        send: { x: width - 8 - 56, y: composeY, w: 56, h: 44 },
      },
      refresh: { x: 8, y: 530, w: width - 16, h: 44 },
      back: { x: 8, y: 584, w: width - 16, h: 44 },
    };
  }
  const composeY = 282;
  return {
    mobile,
    top: { x: 8, y: 4, w: width - 16, h: 20 },
    rail: { x: 8, y: 36, w: 150, h: 276, rowH: 28, gap: 2, cols: 1 },
    pane: { x: 166, y: 36, w: width - 174, h: 240 },
    compose: {
      x: 166,
      y: composeY,
      w: width - 174,
      h: 32,
      input: { x: 166, y: composeY, w: width - 174 - 64, h: 32 },
      send: { x: width - 8 - 56, y: composeY, w: 56, h: 32 },
    },
    refresh: { x: 8, y: 344, w: 304, h: 28 },
    back: { x: 320, y: 344, w: width - 328, h: 28 },
  };
}

function drawTop(ctx, safe, session) {
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // CHAT // LIVE', 8, 4);
  text(
    ctx,
    online ? 'CAST' : 'OFFLINE',
    safe.width - 56,
    4,
    online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM,
  );
}

function drawCastRow(ctx, x, y, w, h, row, selected, mobile) {
  if (selected) {
    ctx.fillStyle = TERMINAL.GREEN_DARK;
    ctx.fillRect(x, y, w, h);
  }
  const color =
    row.status === 'online'
      ? TERMINAL.TEXT_BRIGHT
      : row.status === 'away'
        ? TERMINAL.TEXT
        : TERMINAL.TEXT_DIM;
  const name = fit(row.name, mobile ? 12 : 12);
  text(ctx, `${selected ? '> ' : ' '}${name}`, x + 4, y + (mobile ? 4 : 2), color);
  const label = row.offSchedule ? 'NO SHIFT' : statusLabel(row.status);
  const detail = row.messageCount ? `${label} // ${row.messageCount}` : label;
  text(
    ctx,
    fit(detail, mobile ? 15 : 17),
    x + 4,
    y + (mobile ? 22 : 15),
    row.status === 'online' ? TERMINAL.GREEN_LIGHT : TERMINAL.TEXT_DIM,
  );
  if (row.codec) text(ctx, 'CODEC', x + w - 40, y + (mobile ? 4 : 2), TERMINAL.TEXT_DIM);
  interactiveRegistry.register({
    id: `living-chat-cast-${row.characterId}`,
    x,
    y,
    w,
    h,
    skipInflation: mobile,
    onClick: () => selectLivingChatCharacter(row.characterId),
  });
}

function drawRail(ctx, L) {
  const { rail } = L;
  DopeUI.Panel(ctx, rail.x, rail.y, rail.w, rail.h, 'CAST');
  const rows = buildRail();
  const { cast, signals } = splitRail(rows);
  const ordered = [...cast, ...signals];
  const top = rail.y + 22;

  if (!ordered.length) {
    text(ctx, 'NO SHIFT ON FILE.', rail.x + 10, top, TERMINAL.TEXT_DIM);
    text(ctx, 'NOBODY IS SCHEDULED.', rail.x + 10, top + 16, TERMINAL.TEXT_DIM);
    return;
  }

  if (L.mobile) {
    const cols = rail.cols || 3;
    const gap = rail.gap || 4;
    const rowW = Math.floor((rail.w - 16 - gap * (cols - 1)) / cols);
    const visible = ordered.slice(0, cols * 3);
    visible.forEach((row, index) => {
      const col = index % cols;
      const line = Math.floor(index / cols);
      const x = rail.x + 8 + col * (rowW + gap);
      const y = top + line * (rail.rowH + gap);
      drawCastRow(ctx, x, y, rowW, rail.rowH, row, state.activeId === row.characterId, true);
    });
    if (ordered.length > visible.length) {
      text(ctx, `+${ordered.length - visible.length} MORE`, rail.x + 8, rail.y + rail.h - 18, TERMINAL.TEXT_DIM);
    }
    return;
  }

  const rowH = rail.rowH + rail.gap;
  const maxRows = Math.max(1, Math.floor((rail.h - 28) / rowH));
  const visible = ordered.slice(0, maxRows);
  visible.forEach((row, index) => {
    drawCastRow(
      ctx,
      rail.x + 4,
      top + index * rowH,
      rail.w - 8,
      rail.rowH,
      row,
      state.activeId === row.characterId,
      false,
    );
  });
  if (ordered.length > visible.length) {
    text(ctx, `+${ordered.length - visible.length} MORE`, rail.x + 8, rail.y + rail.h - 18, TERMINAL.TEXT_DIM);
  }
}

function replyTagFor(message) {
  const reply = state.lastReply;
  if (!reply || reply.messageId !== message?.messageId) return '';
  if (reply.characterId && reply.characterId !== state.activeId) return '';
  const label = replyOutcomeLabel(reply.outcome);
  return label ? ` [${label}]` : '';
}

function statusLines(value, maxChars, color = TERMINAL.TEXT_DIM) {
  return wrapText(value, maxChars).map((line, index, all) => ({
    text: line,
    color,
    last: index === all.length - 1,
  }));
}

function buildMessageLines(messages, ownerId, maxChars) {
  const lines = [];
  (Array.isArray(messages) ? messages : []).forEach((message) => {
    const outcome = replyTagFor(message);
    const tags = [];
    if (message.codec) tags.push('CODEC');
    else if (message.exchange) tags.push('EXCHANGE');
    if (outcome) tags.push(outcome.trim().slice(1, -1));
    const tag = tags.length ? ` [${tags.join('][')}]` : '';
    const prefix = `${String(message.senderName || '').toUpperCase()}${tag}: `;
    const body = String(message.body || '');
    const color = message.codec
      ? TERMINAL.TEXT_DIM
      : message.sender === 'player'
        ? TERMINAL.TEXT
        : message.exchange
          ? TERMINAL.TEXT_BRIGHT
          : TERMINAL.GREEN_LIGHT;
    wrapText(`${prefix}${body}`, maxChars).forEach((line, index, all) => {
      lines.push({ text: line, color, last: index === all.length - 1 });
    });
  });

  if (state.sending) {
    lines.push(...statusLines('// HAL IS READING THE LINE...', maxChars));
    if (Date.now() - state.sendStartedAt >= HOLDING_WINDOW_MS) {
      lines.push(...statusLines('// 8-10S WINDOW OPEN // HOLDING LINE DUE.', maxChars));
    }
  } else if (state.noReply && ownerId) {
    const name = characterProfile(state.pack, ownerId).name.toUpperCase();
    lines.push(...statusLines(`// NO REPLY // ${name} IS OFF THE LINE.`, maxChars));
  }
  return lines;
}

function drawTranscript(ctx, time, box, L) {
  const active = state.activeId ? characterProfile(state.pack, state.activeId) : null;
  const maxChars = Math.max(16, Math.floor((box.w - 16) / CELL_W));
  const lines = buildMessageLines(state.messages, state.activeId, maxChars);
  let total = 0;
  lines.forEach((line) => {
    total += LINE_H + (line.last ? MSG_GAP : 0);
  });
  state.maxScroll = Math.max(0, total - box.h);
  state.scrollOffset = Math.max(0, Math.min(state.scrollOffset, state.maxScroll));

  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  let y = total < box.h ? box.y + total : box.y + box.h + state.scrollOffset;
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const line = lines[index];
    y -= LINE_H + (line.last ? MSG_GAP : 0);
    const top = y + (line.last ? MSG_GAP : 0);
    if (top + LINE_H < box.y || top > box.y + box.h) continue;
    text(ctx, line.text, box.x + 6, top, line.color);
  }
  ctx.restore();

  if (state.maxScroll > 0 && !L.mobile) {
    const progress = 1 - state.scrollOffset / state.maxScroll;
    DopeUI.Scrollbar(ctx, box.x + box.w - 6, box.y, 6, box.h, progress, box.h / total);
  }

  if (!state.messages.length && !state.sending && !state.noReply) {
    const headline = L.threadLoading ? 'READING THE CHANNEL...' : 'NO SIGNAL ON FILE.';
    text(ctx, headline, box.x + 8, box.y + 8, TERMINAL.TEXT_DIM);
    if (!state.threadLoading) {
      text(ctx, 'THIS CHANNEL HAS NO MESSAGES YET.', box.x + 8, box.y + 26, TERMINAL.TEXT_DIM);
    }
    if (state.threadError) {
      text(ctx, `// ${state.threadError}`.slice(0, 60), box.x + 8, box.y + 44, TERMINAL.TEXT_BRIGHT);
    }
  } else if (!active) {
    text(ctx, 'NO CHANNEL SELECTED.', box.x + 8, box.y + 8, TERMINAL.TEXT_DIM);
  } else if (state.maxScroll > 0 && state.scrollOffset < 4) {
    text(ctx, 'NEWEST', box.x + box.w - 54, box.y + 2, TERMINAL.TEXT_DIM);
  }
}

function needsDomInput() {
  try {
    return typeof document !== 'undefined' && isMobileDevice();
  } catch {
    return false;
  }
}

function getDomInput(create) {
  if (typeof document === 'undefined') return null;
  let element = document.getElementById(COMPOSE_INPUT_ID);
  if (!element && create) {
    element = document.createElement('input');
    element.id = COMPOSE_INPUT_ID;
    element.type = 'text';
    element.autocomplete = 'off';
    element.autocapitalize = 'sentences';
    element.maxLength = MAX_COMPOSE;
    element.setAttribute('enterkeyhint', 'send');
    element.setAttribute('aria-label', 'Message to the active character');
    element.style.cssText =
      'position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;z-index:5;';
    element.addEventListener('input', () => {
      state.composeText = element.value
        .replace(/[^\x20-\x7e]/g, '')
        .slice(0, MAX_COMPOSE);
      state.composeActive = true;
    });
    element.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (state.composeText.trim()) sendLivingChatMessage(state.composeText);
        element.value = '';
      } else if (event.key === 'Escape') {
        event.preventDefault();
        element.blur();
        state.composeActive = false;
      }
    });
    document.body.appendChild(element);
  }
  return element;
}

export function focusLivingChatCompose() {
  if (composeLockReason(useCoreSession.getState(), state.activeId)) return;
  state.composeActive = true;
  if (!needsDomInput()) return;
  const element = getDomInput(true);
  if (!element) return;
  element.value = state.composeText;
  element.focus({ preventScroll: true });
}

function releaseComposeInput() {
  const element = getDomInput(false);
  if (element) element.blur();
}

function drawCompose(ctx, L, session) {
  const box = L.compose;
  if (!box) return;
  const input = box.input || box;
  const reason = composeLockReason(session, state.activeId);
  const disabled = Boolean(reason);
  const enough = Math.max(8, Math.floor((input.w - 12) / CELL_W));

  ctx.fillStyle = TERMINAL.GREEN_DARK;
  ctx.fillRect(input.x, input.y, input.w, input.h);
  ctx.strokeStyle = disabled ? TERMINAL.GREEN_DARK : TERMINAL.GREEN;
  ctx.strokeRect(input.x, input.y, input.w, input.h);

  let copy;
  let color = TERMINAL.TEXT;
  if (state.sendError) {
    copy = `// ${state.sendError}`;
    color = TERMINAL.TEXT_BRIGHT;
  } else if (disabled) {
    copy = reason;
    color = TERMINAL.TEXT_DIM;
  } else if (state.composeText) {
    copy = `${state.composeText}${state.composeActive ? '_' : ''}`;
  } else {
    copy = state.composeActive ? '_' : 'TYPE A MESSAGE...';
    color = TERMINAL.TEXT_DIM;
  }
  text(ctx, fit(copy, enough), input.x + 6, input.y + (L.mobile ? 14 : 9), color);

  if (!disabled) {
    interactiveRegistry.register({
      id: 'living-chat-compose',
      x: input.x,
      y: input.y,
      w: input.w,
      h: input.h,
      skipInflation: L.mobile,
      focusable: true,
      onClick: focusLivingChatCompose,
    });
  }

  DopeUI.Button(
    ctx,
    box.send.x,
    box.send.y,
    box.send.w,
    box.send.h,
    state.sending ? 'WAIT' : 'SEND',
    disabled ? 'disabled' : 'normal',
  );
  if (!disabled) {
    interactiveRegistry.register({
      id: 'living-chat-send',
      x: box.send.x,
      y: box.send.y,
      w: box.send.w,
      h: box.send.h,
      onClick: () => sendLivingChatMessage(state.composeText),
    });
  }
}

function drawPane(ctx, time, L) {
  const { pane } = L;
  const active = state.activeId ? characterProfile(state.pack, state.activeId) : null;
  const presence = state.activeId ? presenceFor(state.activeId) : null;
  const thread = state.activeId ? threadFor(state.activeId) : null;
  DopeUI.Panel(ctx, pane.x, pane.y, pane.w, pane.h, null);

  const headerH = L.mobile ? 44 : 22;
  text(
    ctx,
    active ? fit(active.name.toUpperCase(), Math.floor((pane.w - 120) / CELL_W)) : 'NO CHANNEL',
    pane.x + 8,
    pane.y + 4,
    TERMINAL.TEXT_BRIGHT,
  );
  const lastSeen =
    presence && presence.status !== 'online' ? formatLastSeen(presence.lastSeenAt) : null;
  const status = presence
    ? lastSeen
      ? `${statusLabel(presence.status)} // ${L.mobile ? lastSeen.slice(5) : `LAST ${lastSeen}`}`
      : statusLabel(presence.status)
    : thread
      ? 'NO SHIFT'
      : '';
  if (status) text(ctx, status, pane.x + 8, pane.y + (L.mobile ? 22 : 15), TERMINAL.TEXT_DIM);
  const flags = [];
  if (active?.codec) flags.push('CODEC');
  if (thread && threadHasExchange(state.messages, state.activeId)) flags.push('EXCHANGE');
  else if (hasCodecIntrusion(state.messages, state.pack)) flags.push('SIGNAL');
  const flagText = flags.join(' // ');
  if (flagText) {
    const flagX = L.mobile
      ? pane.x + 8 + (status ? status.length * CELL_W + 16 : 0)
      : pane.x + pane.w - flagText.length * CELL_W - 8;
    text(ctx, flagText, flagX, pane.y + (L.mobile ? 22 : 15), TERMINAL.TEXT_DIM);
  }

  if (L.mobile) {
    const buttons = [
      { id: 'living-chat-scroll-up', label: '^', x: pane.x + pane.w - 96, delta: 120 },
      { id: 'living-chat-scroll-down', label: 'v', x: pane.x + pane.w - 48, delta: -120 },
    ];
    buttons.forEach((button) => {
      DopeUI.Button(ctx, button.x, pane.y, 44, 44, button.label, 'normal');
      interactiveRegistry.register({
        id: button.id,
        x: button.x,
        y: pane.y,
        w: 44,
        h: 44,
        skipInflation: true,
        onClick: () => {
          state.scrollOffset = Math.max(
            0,
            Math.min(state.scrollOffset + button.delta, state.maxScroll),
          );
        },
      });
    });
  }

  const transcript = {
    x: pane.x + 2,
    y: pane.y + headerH + 2,
    w: pane.w - 4,
    h: pane.h - headerH - 8,
  };
  drawTranscript(ctx, time, transcript, L);
  interactiveRegistry.register({
    id: 'living-chat-transcript',
    x: transcript.x,
    y: transcript.y,
    w: transcript.w,
    h: transcript.h,
    skipInflation: true,
    focusable: false,
    onClick: () => {},
  });
}

function emptyCopy() {
  if (state.v2Off) {
    return ['COMMS CLOSED // V2 OFFLINE', 'THE LIVING CAST OPENS WITH V2.'];
  }
  if (state.error) return [state.error, 'RETRY WHEN THE LINE RETURNS.'];
  if (state.loading || !state.loaded) return ['READING THE CAST...', 'WAITING ON THE LINE.'];
  if (!state.cast.length) {
    return ['NO SHIFT ON FILE.', 'NOBODY IS SCHEDULED FOR THIS DISTRICT.'];
  }
  return ['NO OPEN CHANNELS.', 'PRESENCE IS LIVE. MESSAGES ARRIVE WHEN THE CAST SPEAKS.'];
}

function drawEmpty(ctx, pane) {
  const [headline, detail] = emptyCopy();
  const maxChars = Math.max(12, Math.floor((pane.w - 24) / CELL_W));
  let cursor = pane.y + 24;
  wrapText(headline, maxChars).forEach((line) => {
    text(ctx, line, pane.x + 12, cursor, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    cursor += 16;
  });
  wrapText(detail, maxChars).forEach((line) => {
    text(ctx, line, pane.x + 12, cursor, TERMINAL.TEXT_DIM);
    cursor += 16;
  });
}

function drawFooter(ctx, safe, session, L) {
  const online = session.status === 'online';
  const canRefresh = online && !state.loading;

  if (L.mobile) {
    const note = state.error || state.threadError;
    if (note) text(ctx, `// ${note}`.slice(0, 38), 8, L.refresh.y - 16, TERMINAL.TEXT_BRIGHT);
    DopeUI.Button(ctx, L.refresh.x, L.refresh.y, L.refresh.w, L.refresh.h, state.loading ? 'READING...' : 'REFRESH', canRefresh ? 'hover' : 'disabled');
    if (canRefresh) {
      interactiveRegistry.register({
        id: 'living-chat-refresh',
        x: L.refresh.x,
        y: L.refresh.y,
        w: L.refresh.w,
        h: L.refresh.h,
        onClick: () => loadLivingChat({ force: true }),
      });
    }
    DopeUI.Button(ctx, L.back.x, L.back.y, L.back.w, L.back.h, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'living-chat-back',
      x: L.back.x,
      y: L.back.y,
      w: L.back.w,
      h: L.back.h,
      onClick: () => navigateTo('main_menu'),
    });
    return;
  }

  rule(ctx, 0, 320, safe.width);
  text(ctx, 'PRESENCE LOGGED. NO GHOSTS.', 8, 330, TERMINAL.TEXT_DIM);
  DopeUI.Button(ctx, L.refresh.x, L.refresh.y, L.refresh.w, L.refresh.h, state.loading ? 'READING...' : 'REFRESH', canRefresh ? 'hover' : 'disabled');
  if (canRefresh) {
    interactiveRegistry.register({
      id: 'living-chat-refresh',
      x: L.refresh.x,
      y: L.refresh.y,
      w: L.refresh.w,
      h: L.refresh.h,
      onClick: () => loadLivingChat({ force: true }),
    });
  }
  DopeUI.Button(ctx, L.back.x, L.back.y, L.back.w, L.back.h, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'living-chat-back',
    x: L.back.x,
    y: L.back.y,
    w: L.back.w,
    h: L.back.h,
    onClick: () => navigateTo('main_menu'),
  });
  const note = state.error || state.threadError;
  if (note) text(ctx, `// ${note}`.slice(0, 56), 8, 384, TERMINAL.TEXT_BRIGHT);
}

export function handleLivingChatKey(key) {
  if (typeof document !== 'undefined' && document.activeElement?.id === COMPOSE_INPUT_ID) {
    return false;
  }
  if (key === 'PageUp' || key === 'PageDown') {
    const step = key === 'PageUp' ? 120 : -120;
    state.scrollOffset = Math.max(0, Math.min(state.scrollOffset + step, state.maxScroll));
    return true;
  }
  if (composeLockReason(useCoreSession.getState(), state.activeId)) return false;
  if (key === 'Enter') {
    if (state.composeText.trim()) sendLivingChatMessage(state.composeText);
    return true;
  }
  if (key === 'Backspace') {
    if (state.composeActive) state.composeText = state.composeText.slice(0, -1);
    return true;
  }
  if (key === 'Escape') {
    if (state.composeActive || state.composeText) {
      state.composeActive = false;
      state.composeText = '';
      return true;
    }
    return false;
  }
  if (key.length === 1 && key.charCodeAt(0) >= 32 && key.charCodeAt(0) < 127) {
    state.composeActive = true;
    if (state.composeText.length < MAX_COMPOSE) state.composeText += key;
    return true;
  }
  return false;
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    'wheel',
    (event) => {
      if (window.DCW_CURRENT_VIEW !== 'living_chat') return;
      state.scrollOffset -= event.deltaY * 0.5;
      state.scrollOffset = Math.max(0, Math.min(state.scrollOffset, state.maxScroll));
      event.preventDefault();
    },
    { passive: false },
  );
}

registerView('living_chat', function renderLivingChat(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadLivingChat();
  }
  if (
    state.activeId &&
    state.loaded &&
    state.threadLoadedFor !== state.activeId &&
    !state.threadLoading &&
    !state.threadError
  ) {
    loadLivingChatThread(state.activeId);
  }

  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  const L = layoutLivingChat(safe.width, safe.height);
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);

  drawTop(ctx, safe, session);

  if (session.status !== 'online') {
    DopeUI.Panel(ctx, 8, 36, safe.width - 16, 120, 'NO LINE');
    text(ctx, 'LINE DOWN. LOCAL SHELL.', 20, 70, TERMINAL.TEXT_DIM);
    text(ctx, 'THE LIVING CAST WAITS ON THE NETWORK.', 20, 92, TERMINAL.TEXT_DIM);
  } else if (state.v2Off || state.error || state.loading || !state.loaded || !state.cast.length) {
    drawRail(ctx, L);
    drawEmpty(ctx, L.pane);
  } else {
    drawRail(ctx, L);
    drawPane(ctx, time, L);
  }

  drawCompose(ctx, L, session);
  drawFooter(ctx, safe, session, L);
});

export default 'living_chat';
